import React, { Fragment, useState, useMemo } from 'react'
import { Box, Typography, Divider } from '@mui/material'
import { Button, PopUp, Status, StatusState, StatusVariant, IconName, EmptyState, theme } from '@ketch-com/deck'
import { useDataSentryContext } from 'pages/dataSentry/context/DataSentryContext'
import { ScanDetails, ScanStatus } from '@ketch-com/windlass/dist/dataSentry/v1/scans/scan'
import { format } from 'date-fns'
import { SearchTextInput } from 'components/searchTextInput/SearchTextInput'

interface ScanHistoryModalProps {
  isOpen: boolean
  onClose: () => void
}

type StatusInfo = {
  label: string
  state: StatusState
  icon: IconName
}

const getStatusInfo = (status: ScanStatus | undefined): StatusInfo => {
  switch (status) {
    case 'SCAN_STATUS_COMPLETED':
      return {
        label: 'Scan Complete',
        state: StatusState.success,
        icon: 'FCheckRound' as IconName,
      }
    case 'SCAN_STATUS_FAILED':
      return {
        label: 'Scan Failed',
        state: StatusState.error,
        icon: 'FCrossRound' as IconName,
      }
    case 'SCAN_STATUS_IN_PROGRESS':
      return {
        label: 'Scan In Progress',
        state: StatusState.inProgress,
        icon: 'FSearchItems' as IconName,
      }
    default:
      return {
        label: 'Unknown',
        state: StatusState.warning,
        icon: 'FUnknown' as IconName,
      }
  }
}

export const ScanHistoryModal: React.FC<ScanHistoryModalProps> = ({ isOpen, onClose }) => {
  const { scanHistory, isLoadingScanHistory } = useDataSentryContext()
  const [searchQuery, setSearchQuery] = useState('')

  const filteredScans = useMemo(() => {
    if (!scanHistory?.scans?.length) return []
    return scanHistory.scans.filter(scan => {
      if (!searchQuery) return true

      const query = searchQuery.toLowerCase()
      return scan.propertyCode?.toLowerCase().includes(query)
    })
  }, [scanHistory, searchQuery])

  return (
    <PopUp
      isLoading={isLoadingScanHistory}
      isOpen={isOpen}
      variant="modal"
      onClose={onClose}
      title="Scan History"
      popUpWidth="740px"
      popUpActionChildren={
        <Button variant="outlined" color="primary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <>
        <SearchTextInput
          onChange={nextValue => setSearchQuery(nextValue)}
          value={searchQuery}
          showClearButton
          placeholder="Search"
        />

        {filteredScans.length === 0 && (
          <EmptyState
            size="small"
            colorTheme={theme.palette.Text.Primary}
            iconName="OFilter"
            title="No results found"
            subTitle="Sorry, we couldn't find any results that match your filter criteria. Please adjust your filters and try again."
          />
        )}

        {filteredScans.map((scan: ScanDetails, index: number) => {
          const statusInfo = getStatusInfo(scan?.scanStatus)
          return (
            <Fragment key={scan.runId || index}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" flexDirection="column" gap={0.5}>
                  <Typography variant="label">{format(new Date(scan?.runDate || ''), 'MMM d, yyyy')}</Typography>
                  <Typography>{scan.propertyCode}</Typography>
                  <Typography>{scan.pageUrls?.length.toLocaleString() || '0'} URLs</Typography>
                </Box>
                <Status
                  icon={statusInfo.icon}
                  label={statusInfo.label}
                  variant={StatusVariant.small}
                  status={statusInfo.state}
                />
              </Box>
              {index < (filteredScans?.length || 0) - 1 && <Divider />}
            </Fragment>
          )
        })}
      </>
    </PopUp>
  )
}
